import React, { useState, useMemo, useEffect } from "react";
import { ResultTable, formatCellValue } from "./ResultTable";
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Copy, Table, BarChart2, TrendingUp, Circle, X, SlidersHorizontal, Play } from "lucide-react";
import { MAX_ROWS } from "../../constants";
import { useMobile } from "../../hooks/useMobile";

interface ResultGroup {
  label: string;
  columns: string[];
  rows: Record<string, unknown>[];
}

interface ResultViewerProps {
  rows: Record<string, unknown>[];
  columns: string[];
  error_msg: string;
  resultGroups?: ResultGroup[];
}

const COLORS = ["#0abf53", "#067934", "#5c687c", "#0abf53", "#067934"];

/** Компактная типографика осей/легенды (Recharts по умолчанию слишком крупный). */
const CHART_AXIS_TICK = { fontSize: 10, fill: "#5c687c" };
const CHART_LEGEND_WRAPPER: React.CSSProperties = { fontSize: 11, color: "#5c687c" };

export const ResultViewer = ({ rows, columns, error_msg, resultGroups }: ResultViewerProps) => {
  // Ошибки выводятся в Simulator баннерами/тостами.
  void error_msg;
  const isMobile = useMobile();
  const [mode, setMode] = useState<"table" | "chart">("table");
  const [chartType, setChartType] = useState<"bar" | "line" | "scatter">("bar");
  const [xColumn, setXColumn] = useState<string | null>(null);
  const [yColumns, setYColumns] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string | null>(null);
  const [chartSettingsOpen, setChartSettingsOpen] = useState(false);

  const limitedRows = useMemo(() => rows.slice(0, MAX_ROWS), [rows]);

  const numericColumns = useMemo(
    () => columns.filter((c) => limitedRows.some((r) => !isNaN(Number(r[c])))),
    [columns, limitedRows]
  );

  const availableYColumns = useMemo(
    () => numericColumns.filter((col) => !yColumns.includes(col)),
    [numericColumns, yColumns]
  );

  // Автоматически выбираем оси при переключении в режим графика.
  useEffect(() => {
    if (mode === "chart" && columns.length > 0) {
      if (!xColumn) {
        setXColumn(columns[0]);
      }
      if (yColumns.length === 0 && numericColumns.length > 0) {
        setYColumns([numericColumns[0]]);
      }
    }
  }, [mode, columns, xColumn, yColumns.length, numericColumns]);

  const chartData = useMemo(() => {
    if (!xColumn || yColumns.length === 0) return [];
    const xKey = xColumn as string;
    if (!groupBy) return limitedRows.map((r) => ({ ...r }));

    const dataMap: Record<string, Record<string, unknown>> = {};
    limitedRows.forEach((r) => {
      const xVal = r[xKey] ?? "undefined";
      const xValStr = String(xVal);
      const gVal = String(r[groupBy] ?? "undefined");

      if (!dataMap[xValStr]) {
        // Сохраняем исходное значение X (может быть числом или строкой)
        dataMap[xValStr] = { [xKey]: xVal };
      }
      
      yColumns.forEach((yKey) => {
        const yVal = Number(r[yKey] ?? 0);
        const key = `${gVal}_${yKey}`;
        if (!dataMap[xValStr][key]) dataMap[xValStr][key] = 0;
        const current = Number(dataMap[xValStr][key] ?? 0);
        dataMap[xValStr][key] = current + yVal;
      });
    });

    return Object.values(dataMap);
  }, [limitedRows, xColumn, yColumns, groupBy]);

  const groupSet = useMemo(() => {
    if (!groupBy) return [];
    const set = new Set<string>();
    limitedRows.forEach((r) => set.add(String(r[groupBy] ?? "undefined")));
    return Array.from(set);
  }, [limitedRows, groupBy]);

  /** Заголовок не из домена задачи — строится из выбранных колонок (раньше был хардкод прототипа). */
  const chartTitle = useMemo(() => {
    if (!xColumn || yColumns.length === 0) return "График";
    const yPart =
      yColumns.length <= 2 ? yColumns.join(", ") : `${yColumns.slice(0, 2).join(", ")} +${yColumns.length - 2}`;
    return `${yPart} · ${xColumn}`;
  }, [xColumn, yColumns]);

  const copyMarkdown = () => {
    let md = "| " + columns.join(" | ") + " |\n";
    md += "| " + columns.map(() => "---").join(" | ") + " |\n";
    limitedRows.forEach((r) => {
      md += "| " + columns.map((c) => {
        const formatted = formatCellValue(r[c]);
        // Экранируем символы для Markdown таблиц
        return formatted.replace(/\|/g, "\\|").replace(/\n/g, "<br>");
      }).join(" | ") + " |\n";
    });
    navigator.clipboard.writeText(md);
    alert("Результат скопирован в Markdown!");
  };

  const toggleYColumn = (column: string) => {
    setYColumns((prev) => (prev.includes(column) ? prev.filter((item) => item !== column) : [...prev, column]));
  };

  const chartControlsStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        maxHeight: chartSettingsOpen ? 320 : 0,
        opacity: chartSettingsOpen ? 1 : 0,
        overflow: "hidden",
        transition: "max-height 0.22s ease, opacity 0.18s ease",
      }
    : {
        width: chartSettingsOpen ? 228 : 0,
        minWidth: chartSettingsOpen ? 228 : 0,
        opacity: chartSettingsOpen ? 1 : 0,
        paddingLeft: chartSettingsOpen ? 12 : 0,
        paddingRight: chartSettingsOpen ? 12 : 0,
        borderLeftColor: chartSettingsOpen ? undefined : "transparent",
        pointerEvents: chartSettingsOpen ? "auto" : "none",
        transition:
          "width 0.26s ease, min-width 0.26s ease, padding 0.26s ease, opacity 0.2s ease, border-color 0.2s ease",
      };

  // Если нет данных, показываем пустую таблицу с заголовками
  const isEmpty = !rows || rows.length === 0;

  // Мультизапрос: несколько групп результатов с метками
  if (resultGroups && resultGroups.length > 1) {
    return (
      <div className="flex flex-col gap-6">
        {resultGroups.map((group, idx) => (
          <div key={idx} className="flex flex-col gap-2">
            <div className="text-sm font-medium text-neutral-text border-b border-neutral-border pb-1">
              {group.label}
            </div>
            <ResultViewer rows={group.rows} columns={group.columns} error_msg="" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${isMobile ? "w-full gap-2" : "gap-3"} min-h-0`}>
      {!isEmpty && rows.length > MAX_ROWS && (
        <div className={`text-sm text-neutral-text/70 ${isMobile ? "mb-1" : ""}`}>
          Вывод ограничен: показаны первые {MAX_ROWS} строк из {rows.length}. Чтобы видеть меньше или больше строк, добавьте в конец запроса LIMIT (например, LIMIT 10).
        </div>
      )}
      <div className="bg-neutral-light rounded-2xl overflow-hidden flex flex-col min-h-0 border border-neutral-border/70">
        <div className="h-10 min-w-0 px-2 py-1 bg-neutral-light flex items-center gap-1 border-b border-neutral-border/80">
          <button
            data-onboarding-id="table-chart-toggle"
            className={`h-7 px-3 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              mode === "table"
                ? "bg-white text-neutral-text shadow-sm"
                : "bg-transparent text-neutral-text/70 hover:bg-neutral-border/40 hover:text-neutral-text"
            }`}
            onClick={() => setMode("table")}
          >
            <Table size={14} />
            Таблица
          </button>
          <button
            data-onboarding-id="table-chart-toggle"
            className={`h-7 px-3 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              mode === "chart"
                ? "bg-white text-neutral-text shadow-sm"
                : "bg-transparent text-neutral-text/70 hover:bg-neutral-border/40 hover:text-neutral-text"
            }`}
            onClick={() => setMode("chart")}
          >
            <BarChart2 size={14} />
            График
          </button>

          {mode === "chart" && (
            <div className="inline-flex items-center gap-1 pl-2 ml-1 border-l border-neutral-border/80">
              <button
                className={`h-7 w-7 rounded-md inline-flex items-center justify-center transition-colors ${
                  chartType === "bar" ? "bg-white text-primary shadow-sm" : "text-neutral-text hover:bg-neutral-border/40"
                }`}
                onClick={() => setChartType("bar")}
                title="Столбчатый"
              >
                <BarChart2 size={14} />
              </button>
              <button
                className={`h-7 w-7 rounded-md inline-flex items-center justify-center transition-colors ${
                  chartType === "line" ? "bg-white text-primary shadow-sm" : "text-neutral-text hover:bg-neutral-border/40"
                }`}
                onClick={() => setChartType("line")}
                title="Линейный"
              >
                <TrendingUp size={14} />
              </button>
              <button
                className={`h-7 w-7 rounded-md inline-flex items-center justify-center transition-colors ${
                  chartType === "scatter" ? "bg-white text-primary shadow-sm" : "text-neutral-text hover:bg-neutral-border/40"
                }`}
                onClick={() => setChartType("scatter")}
                title="Точечный"
              >
                <Circle size={14} />
              </button>
            </div>
          )}

          <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1">
            {mode === "chart" && (
              <button
                type="button"
                title={chartSettingsOpen ? "Скрыть настройки графика" : "Показать настройки графика"}
                aria-label={chartSettingsOpen ? "Скрыть настройки графика" : "Показать настройки графика"}
                className="h-7 rounded-lg border border-neutral-border bg-white text-neutral-text inline-flex items-center justify-center gap-1.5 px-2 hover:bg-neutral-light transition-colors lg:px-2.5"
                onClick={() => setChartSettingsOpen((prev) => !prev)}
              >
                <SlidersHorizontal size={14} className="shrink-0" />
                {/* До lg — только иконка + title; с lg — короткий текст (длинные подписи не помещаются в tab bar) */}
                <span className="hidden lg:inline text-xs font-medium truncate max-w-[5.5rem] 2xl:max-w-none">
                  {chartSettingsOpen ? "Скрыть" : "Настройки"}
                </span>
              </button>
            )}
            <button
              className="h-7 w-7 rounded-md inline-flex items-center justify-center text-neutral-text hover:bg-neutral-border/40 transition-colors"
              onClick={copyMarkdown}
              title="Копировать как Markdown"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-b-2xl flex-1 min-h-0 overflow-hidden">
          {mode === "table" ? (
            isEmpty ? (
              <div className="h-full min-h-[140px] flex flex-col items-center justify-center text-neutral-text/50">
                <Play size={34} />
                <div className="mt-2 text-sm font-medium">Запустите запрос, чтобы увидеть результаты</div>
                <div className="mt-1 text-xs font-mono">Ctrl+Enter</div>
              </div>
            ) : (
              <div className="h-full overflow-auto soft-scrollbar">
                <ResultTable rows={limitedRows} columns={columns} onCopyMarkdown={copyMarkdown} isMobile={isMobile} />
              </div>
            )
          ) : (
            <div className={`h-full min-h-0 ${isMobile ? "flex flex-col" : "flex"} overflow-hidden`}>
              <div className="flex-1 min-w-0 min-h-[280px] p-3 flex flex-col gap-2">
                <div className="flex-shrink-0 min-w-0">
                  <div className="text-[13px] font-semibold text-neutral-text truncate" title={chartTitle}>
                    {chartTitle}
                  </div>
                  <div className="text-[11px] text-neutral-text/70 truncate">
                    X: {xColumn ?? "—"} · Группировка: {groupBy ?? "нет"}
                  </div>
                </div>
                <div className="flex-1 min-h-[220px] border border-neutral-border rounded-xl bg-gradient-to-b from-white to-neutral-light/30 p-2">
                  {numericColumns.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8">
                      <BarChart2 className="w-10 h-10 mb-3 text-neutral-text/40" />
                      <p className="text-neutral-text/70 mb-1">Невозможно построить график</p>
                      <p className="text-sm text-neutral-text/50">В результатах нет числовых колонок</p>
                    </div>
                  ) : !xColumn || yColumns.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8">
                      <BarChart2 className="w-10 h-10 mb-3 text-neutral-text/40" />
                      <p className="text-neutral-text/70 mb-1">Выберите поля для графика</p>
                      <p className="text-sm text-neutral-text/50">Укажите ось X и минимум одну колонку Y</p>
                    </div>
                  ) : chartType === "bar" ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xColumn} tick={CHART_AXIS_TICK} tickMargin={4} interval="preserveStartEnd" />
                        <YAxis tick={CHART_AXIS_TICK} tickMargin={4} width={44} />
                        <Tooltip />
                        <Legend wrapperStyle={CHART_LEGEND_WRAPPER} iconSize={10} />
                        {groupBy
                          ? groupSet.flatMap((g) =>
                              yColumns.map((yCol, idx) => (
                                <Bar
                                  key={`${g}_${yCol}`}
                                  dataKey={`${g}_${yCol}`}
                                  name={`${g} - ${yCol}`}
                                  fill={COLORS[(groupSet.indexOf(g) * yColumns.length + idx) % COLORS.length]}
                                />
                              ))
                            )
                          : yColumns.map((yCol, idx) => (
                              <Bar key={yCol} dataKey={yCol} name={yCol} fill={COLORS[idx % COLORS.length]} />
                            ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : chartType === "line" ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xColumn} tick={CHART_AXIS_TICK} tickMargin={4} interval="preserveStartEnd" />
                        <YAxis tick={CHART_AXIS_TICK} tickMargin={4} width={44} />
                        <Tooltip />
                        <Legend wrapperStyle={CHART_LEGEND_WRAPPER} iconSize={10} />
                        {groupBy
                          ? groupSet.flatMap((g) =>
                              yColumns.map((yCol, idx) => (
                                <Line
                                  key={`${g}_${yCol}`}
                                  type="monotone"
                                  dataKey={`${g}_${yCol}`}
                                  name={`${g} - ${yCol}`}
                                  stroke={COLORS[(groupSet.indexOf(g) * yColumns.length + idx) % COLORS.length]}
                                />
                              ))
                            )
                          : yColumns.map((yCol, idx) => (
                              <Line key={yCol} type="monotone" dataKey={yCol} name={yCol} stroke={COLORS[idx % COLORS.length]} />
                            ))}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart data={chartData}>
                        <CartesianGrid />
                        <XAxis
                          dataKey={xColumn}
                          type={numericColumns.includes(xColumn) ? "number" : "category"}
                          tick={CHART_AXIS_TICK}
                          tickMargin={4}
                          interval="preserveStartEnd"
                        />
                        <YAxis tick={CHART_AXIS_TICK} tickMargin={4} width={44} />
                        <Tooltip />
                        <Legend wrapperStyle={CHART_LEGEND_WRAPPER} iconSize={10} />
                        {groupBy
                          ? groupSet.flatMap((g) =>
                              yColumns.map((yCol, idx) => (
                                <Scatter
                                  key={`${g}_${yCol}`}
                                  name={`${g} - ${yCol}`}
                                  data={chartData}
                                  dataKey={`${g}_${yCol}`}
                                  fill={COLORS[(groupSet.indexOf(g) * yColumns.length + idx) % COLORS.length]}
                                />
                              ))
                            )
                          : yColumns.map((yCol, idx) => (
                              <Scatter key={yCol} data={chartData} dataKey={yCol} name={yCol} fill={COLORS[idx % COLORS.length]} />
                            ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div
                className={`shrink-0 bg-[#fcfcfd] border-l border-neutral-border/80 ${isMobile ? "border-l-0 border-t" : ""}`}
                style={chartControlsStyle}
              >
                <div className="h-full overflow-auto py-3 flex flex-col gap-3 soft-scrollbar">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-text/60 mb-1.5">Ось X</div>
                    <select
                      className="w-full h-9 px-2.5 border border-neutral-border rounded text-xs text-neutral-text bg-white"
                      value={xColumn ?? ""}
                      onChange={(e) => setXColumn(e.target.value || null)}
                    >
                      {columns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-text/60 mb-1.5">Ось Y</div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {yColumns.map((col) => (
                        <button
                          key={col}
                          className="h-6 px-2 rounded-full text-[11px] bg-primary-light text-primary-dark inline-flex items-center gap-1"
                          onClick={() => toggleYColumn(col)}
                          title="Убрать"
                        >
                          {col}
                          <X size={12} />
                        </button>
                      ))}
                      {yColumns.length === 0 && <span className="text-xs text-neutral-text/50">Не выбрано</span>}
                    </div>
                    <select
                      className="w-full h-9 px-2.5 border border-neutral-border rounded text-xs text-neutral-text bg-white"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          toggleYColumn(e.target.value);
                        }
                      }}
                    >
                      <option value="">Добавить колонку...</option>
                      {availableYColumns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-text/60 mb-1.5">Группировка</div>
                    <select
                      className="w-full h-9 px-2.5 border border-neutral-border rounded text-xs text-neutral-text bg-white"
                      value={groupBy ?? ""}
                      onChange={(e) => setGroupBy(e.target.value || null)}
                    >
                      <option value="">Без группировки</option>
                      {columns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
